/**
 * B428 (h)-(j) — the ask bar's ANSWER PAGE, the blessed frames
 * (apps/design #answerpages, Pathik 2026-09-09: "looks good") as ONE
 * shared component. Anatomy, exactly the frames': the "Ask anything ·
 * <archetype>" eyebrow, the operator's question verbatim in quotes, the
 * answer SENTENCE, the shape-led body (the caller composes stat cards /
 * chart / table from the answer's DATA SHAPE — presentation is part of
 * the answer, not a fixed template), SUGGESTED FOLLOW-UPS as chips, a
 * persistent ask field so the conversation continues in place, and the
 * "How this was answered" footnote naming the read.
 *
 * In the package (not hand-rolled in an app) per the 2026-07-26 UI law:
 * the admin's real page and the design gallery's exemplars render THIS,
 * so the blessed spec and the shipped screen cannot drift apart.
 */
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { calendarWords } from "@transact/domain";
import type { ReactNode } from "react";
import { Button, Chip, DateRangeField, GlassCard, Input } from "./components.js";
import { Icon } from "./icons.js";
import { RightSheet } from "./sheet.js";

/** One big-figure card of a Figure answer (the frames' stat anatomy). */
export function AnswerStat(props: {
  readonly label: string;
  readonly figure: string;
  readonly note?: string;
}): ReactNode {
  return (
    <GlassCard label={props.label}>
      <p className="t-answerstat-figure">{props.figure}</p>
      {props.note !== undefined ? <p className="t-answerstat-note">{props.note}</p> : null}
    </GlassCard>
  );
}

export interface AnswerFollowUp {
  readonly label: string;
  readonly onPick: () => void;
}

/** The frames' follow-up block: chips + the persistent ask field. */
function AnswerPageFollowUps(props: {
  readonly followUps: readonly AnswerFollowUp[];
  readonly onAsk?: ((text: string) => void) | undefined;
  readonly askId: string;
}): ReactNode {
  const [text, setText] = useState("");
  return (
    <div className="t-answerpage-followups">
      {props.followUps.length > 0 ? (
        <>
          <span className="t-answerpage-followups-title">Suggested follow-ups</span>
          <div className="t-answerpage-chips">
            {props.followUps.map((f) => (
              <Chip key={f.label} label={f.label} pressed={false} onPress={f.onPick} />
            ))}
          </div>
        </>
      ) : null}
      {props.onAsk !== undefined ? (
        <form
          className="t-answerpage-ask"
          onSubmit={(e) => {
            e.preventDefault();
            const q = text.trim();
            if (q === "") return;
            props.onAsk?.(q);
            setText("");
          }}
        >
          <Input
            id={props.askId}
            label="Ask a follow-up"
            placeholder="Ask further — the answer opens on this page"
            value={text}
            onChange={setText}
          />
        </form>
      ) : null}
    </div>
  );
}

/**
 * The full answer-page canvas. `children` is the shape-led body the
 * caller composed from the answer's data (stat-card grid, chart, table
 * — the blessed frames' three archetypes); `busy` marks a body still
 * being read, so a page is honest while the wire answers.
 */
export function AnswerPage(props: {
  /** The frames' eyebrow tail — "Figure answer" / "Series answer" / "Rows answer". */
  readonly archetype: string;
  /** The operator's question, verbatim — the page quotes it. */
  readonly question: string;
  /** The answer in one operator sentence (the numbers live in the body). */
  readonly sentence: string;
  /** "How this was answered — …": the named read behind the numbers. */
  readonly how: string;
  readonly children?: ReactNode;
  readonly followUps?: readonly AnswerFollowUp[];
  /** Present = the persistent ask field renders and submits here. */
  readonly onAsk?: ((text: string) => void) | undefined;
  /** Distinguishes ask fields when two pages ever coexist (tests, gallery). */
  readonly askId?: string;
  readonly busy?: boolean;
}): ReactNode {
  return (
    <article className="t-answerpage" aria-busy={props.busy === true ? true : undefined}>
      <div>
        <span className="t-answerpage-eyebrow">{`Ask anything · ${props.archetype}`}</span>
        <h1 className="t-answerpage-q">{`“${props.question}”`}</h1>
        <p className="t-answerpage-sentence">{props.sentence}</p>
      </div>
      {props.children}
      <AnswerPageFollowUps
        followUps={props.followUps ?? []}
        onAsk={props.onAsk}
        askId={props.askId ?? "answer-followup-ask"}
      />
      <p className="t-footnote">{props.how}</p>
    </article>
  );
}

/* ------------------------------------------------------------------ B460
   (a) — the CONVERSATION: /ask no longer replaces the answer on every
   ask. Each settled ask is one AnswerExchange block, appended in order
   inside an AnswerThreadPage whose composer stays pinned at the foot.

   B864 (Pathik 2026-10-16/17, the frames 723:2 / 723:157 / 724:2 / 726:2,
   approved "looks great"): the exchange is TWO MESSAGE ROWS — the
   operator's line beside their avatar, the assistant's beside its spark —
   in an 880 column; the question is said plainly (no eyebrow, no quotes),
   the provenance folds under "How this was answered", and the composer is
   a card pinned to the viewport's foot over a fade, with the latest
   follow-ups as cards above it. In the package per the 2026-07-26 UI law:
   the admin's thread and the gallery's exemplar render THIS. */

/** Who is asking — the initials the operator's avatar wears (the thread provides it). */
const ThreadWhoContext = createContext<string>("You");

/* ------------------------------------------------------------------ B890
   The composer's + CONTEXT DOOR (frame 723:157 / 721:2): a 40 × 40
   radius-8 square at the bar's left opens the picker (the house sheet) —
   what is on this page, a record the operator names, a date range. Each
   pick is a removable context chip under the composer; the CALLER owns the
   picks and sends them with the next ask (agent.run `context`). A record is
   the operator's own words for it ("Sunset cruise", "HARBOR10"): the
   assistant finds it with its own reads, as it would from the question. */

/** One pick: its key (what it is) and the words it is shown and sent in. */
export interface AskContextPick {
  readonly key: string;
  readonly words: string;
}

/** The composer's context, owned by the screen that sends the ask. */
export interface AskContext {
  readonly picks: readonly AskContextPick[];
  readonly onAdd: (pick: AskContextPick) => void;
  readonly onRemove: (key: string) => void;
  /** What is on this page ("On Promotions"), offered as the pick keyed "page". */
  readonly page?: string | undefined;
}

/** The picks one question may carry beside the page (agent.run's `context` ceiling). */
export const ASK_CONTEXT_MAX = 4;

export type AskPickOutcome = { readonly ok: true; readonly pick: AskContextPick } | { readonly ok: false; readonly why: string };

/** A record the operator names — trimmed, at most 100 characters. */
export function recordPick(typed: string): AskPickOutcome {
  const words = typed.trim();
  if (words === "") return { ok: false, why: "Type the record's code or name first." };
  if (words.length > 100) return { ok: false, why: "That is longer than a code or a name — 100 characters at most." };
  return { ok: true, pick: { key: `record:${words}`, words: `Record · ${words}` } };
}

/** "2026-10-01" in the one US calendar register (B663): "Oct 1, 2026". */
const dayWords = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  return calendarWords(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1), { timeZone: "UTC", style: "monthDay", year: true });
};

/** A date range ("2026-10-01" … "2026-10-07"), its refusal named at the end it belongs to. */
export function datesPick(from: string, until: string): AskPickOutcome | { readonly ok: false; readonly at: "from" | "until"; readonly why: string } {
  if (from === "") return { ok: false, at: "from", why: "Pick the first day." };
  if (until === "") return { ok: false, at: "until", why: "Pick the last day." };
  if (until < from) return { ok: false, at: "until", why: "The last day is before the first — pick a later one." };
  const words = from === until ? `Dates · ${dayWords(from)}` : `Dates · ${dayWords(from)} – ${dayWords(until)}`;
  return { ok: true, pick: { key: `dates:${from}:${until}`, words } };
}

/** The picker behind the + door: the page, a record, a date range. */
function ContextPicker(props: { readonly askId: string; readonly context: AskContext; readonly onClose: () => void }): ReactNode {
  const { context } = props;
  const id = `${props.askId}-context`;
  const [record, setRecord] = useState("");
  const [recordWhy, setRecordWhy] = useState<string | undefined>(undefined);
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");
  const [datesWhy, setDatesWhy] = useState<{ readonly at: "from" | "until"; readonly why: string } | undefined>(undefined);
  const own = context.picks.filter((p) => p.key !== "page").length;
  const full = own >= ASK_CONTEXT_MAX ? `${String(ASK_CONTEXT_MAX)} is the most one question carries — remove one first.` : undefined;
  const pageIn = context.picks.some((p) => p.key === "page");
  const addRecord = (): void => {
    const out = recordPick(record);
    if (!out.ok) return setRecordWhy(out.why);
    if (full !== undefined) return setRecordWhy(full);
    context.onAdd(out.pick);
    setRecord("");
    setRecordWhy(undefined);
  };
  const addDates = (): void => {
    const out = datesPick(from, until);
    if (!out.ok) return setDatesWhy("at" in out ? { at: out.at, why: out.why } : { at: "from", why: out.why });
    if (full !== undefined) return setDatesWhy({ at: "until", why: full });
    context.onAdd(out.pick);
    setFrom("");
    setUntil("");
    setDatesWhy(undefined);
  };
  return (
    <RightSheet
      id={id}
      open
      kicker="The assistant"
      title="Add context"
      lede="What you add rides your next question — the assistant reads it before it answers."
      onClose={props.onClose}
      actions={
        <Button variant="primary" onClick={props.onClose}>
          Done
        </Button>
      }
    >
      <div className="t-contextpicker">
        {context.page !== undefined ? (
          <section className="t-contextpicker-row" aria-labelledby={`${id}-page`}>
            <h3 className="t-contextpicker-head" id={`${id}-page`}>
              What is on this page
            </h3>
            {pageIn ? (
              <p className="t-contextpicker-fact">{`${context.page} — already added. Remove its chip to ask without it.`}</p>
            ) : (
              <Button variant="neutral" onClick={() => context.onAdd({ key: "page", words: context.page as string })}>
                {`Add ${context.page}`}
              </Button>
            )}
          </section>
        ) : null}
        <section className="t-contextpicker-row" aria-labelledby={`${id}-record-head`}>
          <h3 className="t-contextpicker-head" id={`${id}-record-head`}>
            A record
          </h3>
          <Input
            id={`${props.askId}-record`}
            label="Its code or name"
            value={record}
            onChange={(v) => {
              setRecord(v);
              setRecordWhy(undefined);
            }}
            hint="As you would say it — the assistant finds it."
            placeholder="Sunset cruise, HARBOR10, the Smith order"
            error={recordWhy}
          />
          <Button variant="neutral" onClick={addRecord}>
            Add record
          </Button>
        </section>
        <section className="t-contextpicker-row">
          <DateRangeField
            id={`${props.askId}-dates`}
            legend="A date range"
            joiner="to"
            hint="The days the question is about — one day is the same day twice."
            from={{ label: "First day", value: from, onChange: (v) => { setFrom(v); setDatesWhy(undefined); }, error: datesWhy?.at === "from" ? datesWhy.why : undefined }}
            until={{ label: "Last day", value: until, onChange: (v) => { setUntil(v); setDatesWhy(undefined); }, error: datesWhy?.at === "until" ? datesWhy.why : undefined }}
          />
          <Button variant="neutral" onClick={addDates}>
            Add dates
          </Button>
        </section>
      </div>
    </RightSheet>
  );
}

/** The pinned ask form — one mint for the page and the thread. */
export function AskField(props: {
  readonly onAsk: (text: string) => void;
  readonly askId: string;
  readonly label?: string;
  readonly placeholder?: string;
  readonly busy?: boolean;
  /** B890 — the + door and its chips; absent = the send door alone. */
  readonly context?: AskContext | undefined;
}): ReactNode {
  const [text, setText] = useState("");
  const [picking, setPicking] = useState(false);
  const send = (): void => {
    const q = text.trim();
    if (q === "") return;
    props.onAsk(q);
    setText("");
  };
  const context = props.context;
  return (
    <>
      <form
        className="t-answerpage-ask t-composer"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <label className="t-visually-hidden" htmlFor={props.askId}>
          {props.label ?? "Ask a follow-up"}
        </label>
        <input
          id={props.askId}
          className="t-composer-field"
          type="text"
          autoComplete="off"
          placeholder={props.placeholder ?? "Ask anything"}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
        />
        <div className="t-composer-bar">
          {context !== undefined ? (
            <button type="button" className="t-composer-plus" aria-label="Add context" aria-haspopup="dialog" aria-expanded={picking} onClick={() => setPicking(true)}>
              <Icon name="plus" size={20} />
            </button>
          ) : null}
          <button type="submit" className="t-composer-send" aria-label={props.busy === true ? "The assistant is working" : "Send"} aria-busy={props.busy === true ? true : undefined} data-empty={text.trim() === "" ? "true" : undefined}>
            <span aria-hidden="true">{props.busy === true ? "■" : "→"}</span>
          </button>
        </div>
      </form>
      {context !== undefined && context.picks.length > 0 ? (
        <div className="t-composer-context">
          {context.picks.map((p) => (
            <span key={p.key} className="t-contextchip">
              <span className="t-contextchip-dot" aria-hidden="true" />
              {p.words}
              <button type="button" className="t-contextchip-x" aria-label={`Remove ${p.words}`} onClick={() => context.onRemove(p.key)}>
                <span aria-hidden="true">×</span>
              </button>
            </span>
          ))}
        </div>
      ) : null}
      {context !== undefined && picking ? <ContextPicker askId={props.askId} context={context} onClose={() => setPicking(false)} /> : null}
    </>
  );
}

/** One message row: an avatar beside a body column (the frames' 32 / 24 / column). */
function MessageRow(props: { readonly who: "you" | "assistant"; readonly children: ReactNode }): ReactNode {
  const initials = useContext(ThreadWhoContext);
  return (
    <div className={`t-msg t-msg--${props.who}`}>
      <span className={`t-msg-avatar t-msg-avatar--${props.who}`} aria-hidden="true">
        {props.who === "you" ? initials : "✦"}
      </span>
      <div className="t-msg-body">{props.children}</div>
    </div>
  );
}

/**
 * One question-and-answer block of the conversation: the operator's row,
 * then the assistant's (sentence, the shape-led body, its still-pressable
 * follow-up chips, the provenance folded). Earlier exchanges stay fully
 * readable.
 */
/**
 * B864 (723:2, turn 2) — a form's reply ("Promotion name: Summer sale\nKind:
 * Percent off …") is said back as CHIPS, one per field, not as a run-on
 * line. Anything else the operator typed stays a plain question.
 */
export function formReplyOf(question: string): readonly { readonly field: string; readonly value: string }[] | undefined {
  if (!question.includes("\n")) return undefined;
  const pairs = question
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .map((line) => {
      const m = /^([^:]{1,60}):\s+(.+)$/.exec(line);
      return m === null ? undefined : { field: m[1]!.trim(), value: m[2]!.trim() };
    });
  if (pairs.length < 2 || pairs.some((p) => p === undefined)) return undefined;
  return pairs as { readonly field: string; readonly value: string }[];
}

export function AnswerExchange(props: {
  readonly archetype: string;
  readonly question: string;
  readonly sentence: string;
  readonly how: string;
  readonly children?: ReactNode;
  readonly followUps?: readonly AnswerFollowUp[];
  /** What the ask was made on — the page, the thing on screen (B852 e) — shown as chips under the question. */
  readonly context?: readonly string[];
  readonly busy?: boolean;
}): ReactNode {
  const followUps = props.followUps ?? [];
  const said = formReplyOf(props.question);
  return (
    <section className="t-answerexchange" data-archetype={props.archetype} aria-busy={props.busy === true ? true : undefined}>
      <MessageRow who="you">
        {said === undefined ? (
          <h2 className="t-answerpage-q t-answerexchange-q">{props.question}</h2>
        ) : (
          <h2 className="t-answerpage-q t-answerexchange-q t-answerexchange-q--said" aria-label={props.question}>
            {said.map((pair) => (
              <span key={pair.field} className="t-saidchip">
                <span className="t-saidchip-field">{pair.field}</span>
                {pair.value}
              </span>
            ))}
          </h2>
        )}
        {props.context !== undefined && props.context.length > 0 ? (
          <div className="t-msg-context">
            {props.context.map((c) => (
              <span key={c} className="t-contextchip">
                <span className="t-contextchip-dot" aria-hidden="true" />
                {c}
              </span>
            ))}
          </div>
        ) : null}
      </MessageRow>
      <MessageRow who="assistant">
        {props.sentence !== "" ? <p className="t-answerpage-sentence">{props.sentence}</p> : null}
        {props.children}
        {followUps.length > 0 ? (
          <div className="t-answerpage-followups t-answerexchange-followups">
            <span className="t-answerpage-followups-title">Suggested follow-ups</span>
            <div className="t-answerpage-chips">
              {followUps.map((f) => (
                <Chip key={f.label} label={f.label} pressed={false} onPress={f.onPick} />
              ))}
            </div>
          </div>
        ) : null}
        {props.how !== "" ? (
          <details className="t-answer-how">
            <summary className="t-answer-how-fold">How this was answered</summary>
            <p className="t-footnote">{props.how}</p>
          </details>
        ) : null}
      </MessageRow>
    </section>
  );
}

/** A follow-up offered as a card above the composer (the frames' DRILL DOWN / DO NEXT pair). */
export interface AnswerFollowUpCard extends AnswerFollowUp {
  readonly kicker: string;
}

/**
 * The conversation canvas: exchanges appended in order (children), the
 * composer pinned to the foot with the latest follow-ups as cards above
 * it. `door` renders beside the eyebrow — the history door and its kin. A
 * read-only replay simply passes no onAsk and provides its own way back.
 */
export function AnswerThreadPage(props: {
  /** The page eyebrow tail — "Conversation" live, "Stored conversation" in replay. */
  readonly archetype?: string;
  readonly door?: ReactNode;
  readonly children: ReactNode;
  readonly onAsk?: ((text: string) => void) | undefined;
  readonly askId?: string;
  readonly askLabel?: string;
  readonly busy?: boolean;
  /** The operator's initials for their avatar ("PS"); "You" when the shell has no name. */
  readonly who?: string;
  /** The latest answer's follow-ups, offered as cards above the composer. */
  readonly followUpCards?: readonly AnswerFollowUpCard[];
  /** B864 — the session rail beside the thread (open); absent = the thread alone. */
  readonly rail?: ReactNode;
  /** B890 — the composer's + door and its chips. */
  readonly askContext?: AskContext | undefined;
}): ReactNode {
  const cards = props.followUpCards ?? [];
  // B864 — the newest exchange comes into view when it appears (the dock is
  // fixed at the foot, so the thread itself has to move): the first paint stays put.
  const itemsRef = useRef<HTMLDivElement | null>(null);
  const newestRef = useRef<Element | null | undefined>(undefined);
  useEffect(() => {
    const items = itemsRef.current;
    if (items === null) return;
    const all = items.querySelectorAll(".t-answerexchange");
    const newest = all.length === 0 ? null : all[all.length - 1] ?? null;
    const first = newestRef.current === undefined;
    if (!first && newest !== null && newest !== newestRef.current && typeof newest.scrollIntoView === "function") {
      newest.scrollIntoView({ block: "start", behavior: "smooth" });
    }
    newestRef.current = newest;
  });
  const article = (
      <article className="t-answerthread" aria-busy={props.busy === true ? true : undefined}>
        <div className="t-answerthread-head">
          <span className="t-answerpage-eyebrow">{`Ask anything · ${props.archetype ?? "Conversation"}`}</span>
          {props.door}
        </div>
        <div className="t-answerthread-items" ref={itemsRef}>{props.children}</div>
        {props.onAsk !== undefined ? (
          <div className="t-answerthread-ask t-answerthread-dock">
            {cards.length > 0 ? (
              <div className="t-followcards">
                {cards.map((c) => (
                  <button key={c.label} type="button" className="t-followcard" onClick={c.onPick}>
                    <span className="t-followcard-kicker">{c.kicker}</span>
                    <span className="t-followcard-q">{c.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
            <AskField onAsk={props.onAsk} askId={props.askId ?? "answer-thread-ask"} {...(props.askLabel !== undefined ? { label: props.askLabel } : {})} {...(props.busy === true ? { busy: true } : {})} context={props.askContext} />
          </div>
        ) : null}
      </article>
  );
  return (
    <ThreadWhoContext.Provider value={props.who ?? "You"}>
      {props.rail !== undefined ? (
        <div className="t-answerthread-layout">
          {article}
          {props.rail}
        </div>
      ) : (
        article
      )}
    </ThreadWhoContext.Provider>
  );
}
